import React, { useState, useEffect } from 'react';
import Icon13 from "../../img/7481d4f175f497fa3ca635d9ea8974d559a00bf54762cdfe73ec49ac971c04f3.svg";

// Add this helper component for tooltip cells
function TooltipCell({ content, className }) {
  return (
    <td 
      className={`px-6 py-4 text-sm truncate relative group ${className}`}
      title={content} // Native HTML tooltip
    >
      <span className="truncate block">{content}</span>
      <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded-md text-xs whitespace-normal max-w-xs -translate-y-full top-0 left-1/2 transform -translate-x-1/2">
        {content}
      </div>
    </td>
  );
}

export function ManageContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'Name', direction: 'asc' });
  
  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await fetch('https://projects-dev.nettlescs.com:5000/api/salesforce/all-contacts');
      const data = await response.json();
      setContacts(data.records);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setLoading(false);
    }
  };

  // Sorting function
  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  // Filter contacts based on search term
  const filteredContacts = contacts.filter(contact => {
    const searchStr = searchTerm.toLowerCase();
    return (
      contact.Name?.toLowerCase().includes(searchStr) ||
      contact.Account.Name?.toLowerCase().includes(searchStr) ||
      contact.Email?.toLowerCase().includes(searchStr) ||
      contact.MobilePhone?.toLowerCase().includes(searchStr) ||
      contact.GMB_Type__c?.toLowerCase().includes(searchStr) ||
      contact.GMB_Region__c?.toLowerCase().includes(searchStr)
    );
  });

  // Sort filtered contacts
  const sortedContacts = [...filteredContacts].sort((a, b) => {
    if (!a[sortConfig.key] || !b[sortConfig.key]) return 0;
    
    const aValue = a[sortConfig.key].toLowerCase();
    const bValue = b[sortConfig.key].toLowerCase();
    
    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Get current page data
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentContacts = sortedContacts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedContacts.length / ITEMS_PER_PAGE);

  // Column configuration with fixed widths
  const columns = [
    { key: 'Name', label: 'Name', width: '15%' },
    { key: 'AccountId', label: 'Account Name', width: '20%' },
    { key: 'Email', label: 'Email', width: '25%' },
    { key: 'MobilePhone', label: 'Mobile Phone', sortable: false, width: '10%' },
    { key: 'GMB_Type__c', label: 'Type', width: '10%' },
    { key: 'GMB_Region__c', label: 'Region', width: '10%' }
  ];

  return (
    <div className="flex flex-col w-full">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Manage Contacts</h2>
      </div>

      <div className="bg-white rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <form className="flex gap-2.5 items-center px-5 py-3 text-lg font-light bg-stone-50 rounded-[31px] text-zinc-500 w-[30%]">
              <img
                loading="lazy"
                src={Icon13}
                className="object-contain shrink-0 self-stretch my-auto aspect-square w-[27px]"
                alt=""
              />
              <input
                type="search"
                id="contactSearch"
                placeholder="Search"
                className="bg-transparent border-none outline-none w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </form>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th 
                      key={column.key}
                      style={{ width: column.width }}
                      className={`px-6 py-3 text-left text-sm font-medium text-gray-500 ${
                        !column.sortable ? '' : 'cursor-pointer'
                      } whitespace-nowrap`}
                      onClick={() => column.sortable !== false && handleSort(column.key)}
                    >
                      <div className="flex items-center">
                        <span className="truncate">{column.label}</span>
                        {column.sortable !== false && (
                          <span className="ml-1 inline-block w-4">
                            {sortConfig.key === column.key ? 
                              (sortConfig.direction === 'asc' ? '↓' : '↑') : 
                              '↕'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500" style={{ width: '5%' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">Loading...</td>
                  </tr>
                ) : currentContacts.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">No contacts found</td>
                  </tr>
                ) : (
                  currentContacts.map((contact) => (
                    <tr key={contact.Id} className="hover:bg-gray-50">
                      <TooltipCell 
                        content={contact.Name}
                        className="text-gray-900"
                      />
                      <TooltipCell 
                        content={contact.Account.Name}
                        className="text-gray-900"
                      />
                      <TooltipCell 
                        content={contact.Email}
                        className="text-gray-900"
                      />
                      <TooltipCell 
                        content={contact.MobilePhone}
                        className="text-gray-900"
                      />
                      <TooltipCell 
                        content={contact.GMB_Type__c}
                        className="text-gray-900"
                      />
                      <TooltipCell 
                        content={contact.GMB_Region__c}
                        className="text-gray-900"
                      />
                      <td className="px-6 py-4 text-sm text-blue-600 hover:text-blue-800 cursor-pointer whitespace-nowrap">
                        Edit
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex justify-between items-center mt-4 px-6">
              <div className="text-sm text-gray-700">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedContacts.length)} of {sortedContacts.length} results
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}